import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { ColorPickerHex } from "@/components/_molecules/color-picker/color-picker-hex";

import { UpdateLabelAction } from "./label-action-button/actions/update-label-action";

export interface LabelEditFormProps {
  label: {
    id: string;
    title: string;
    color: string;
  };
  className?: string;
  onSuccess?: () => void;
  onCanceled?: () => void;
}

export const LabelEditForm: FC<LabelEditFormProps> = ({ label, onSuccess, onCanceled }) => {
  const { t } = useTranslation("common");
  const ref = useRef<HTMLInputElement>(null);
  const [labelName, setLabelName] = useState(label.title);
  const [labelColor, setLabelColor] = useState(label.color);

  const updatedLabel = useMemo(() => {
    return {
      id: label.id,
      title: labelName,
      color: labelColor,
    };
  }, [label, labelName, labelColor]);

  function onUpdateCompleted() {
    if (onSuccess) {
      onSuccess();
    }
  }

  function onUpdateError() {
    toast.error(t("errors.title"), { description: t("errors.generic") });
  }

  useEffect(() => {
    setLabelName(label.title);
    setLabelColor(label.color);
    // Intentionally only depend on label.id — reset state when a different label is selected, not when title/color change (which would overwrite user edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label.id]);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div className="flex w-full flex-row items-center justify-between gap-2 p-1">
      <ColorPickerHex value={labelColor} onChange={(color) => setLabelColor(color)} />
      <div className="grow">
        <Input
          ref={ref}
          autoFocus
          value={labelName}
          onChange={(e) => setLabelName(e.currentTarget.value)}
          placeholder={t("label.namePlaceholder")}
          aria-label={t("label.namePlaceholder")}
          className="bg-background focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
        />
      </div>
      <div>
        <Button variant="outline" size="sm" className="cursor-pointer" onClick={onCanceled}>
          {t("actions.cancel")}
        </Button>
      </div>
      <div>
        <UpdateLabelAction label={updatedLabel} onSuccess={onUpdateCompleted} onError={onUpdateError} asChild>
          <Button variant="default" size="sm" className="cursor-pointer" disabled={labelName === ""}>
            {t("actions.save")}
          </Button>
        </UpdateLabelAction>
      </div>
    </div>
  );
};
